import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "./AuthShell";
import { Btn, Field, Spinner } from "@/components/ui-kit";
import { api, formatError } from "@/api";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => setValid(r.data.valid))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Salasanan tulee olla vähintään 6 merkkiä."); return; }
    if (form.password !== form.confirm) { setError("Salasanat eivät täsmää."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Salasana vaihdettu!");
      setTimeout(() => navigate("/kirjaudu", { replace: true }), 2500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (checking) return <Spinner />;

  if (done) {
    return (
      <AuthShell title="Valmista!" subtitle="Salasanasi on vaihdettu." testid="reset-password-page">
        <div className="space-y-6" data-testid="reset-success">
          <div className="flex items-start gap-3 rounded-xl border border-[#E6E1D9] bg-[#E9EAEC] p-4">
            <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-[#292827] leading-relaxed">Salasana vaihdettu onnistuneesti. Ohjaamme sinut kirjautumiseen…</p>
          </div>
          <Btn className="w-full" onClick={() => navigate("/kirjaudu")} data-testid="go-login">Kirjaudu sisään</Btn>
        </div>
      </AuthShell>
    );
  }

  if (!valid) {
    return (
      <AuthShell title="Linkki ei kelpaa" subtitle="Palautuslinkki on virheellinen tai vanhentunut." testid="reset-password-page">
        <div className="space-y-6" data-testid="reset-invalid">
          <div className="flex items-start gap-3 rounded-xl border border-[#E6E1D9] bg-[#E9EAEC] p-4">
            <AlertCircle size={20} className="text-[#292827] shrink-0 mt-0.5" />
            <p className="text-sm text-[#292827] leading-relaxed">Tämä palautuslinkki on virheellinen, jo käytetty tai vanhentunut. Pyydä uusi palautuslinkki.</p>
          </div>
          <Btn className="w-full" onClick={() => navigate("/unohtui-salasana")} data-testid="request-new-link">Pyydä uusi linkki</Btn>
          <p className="text-center text-sm text-[#6E675C]">
            <span className="font-medium text-[#292827] cursor-pointer hover:underline" onClick={() => navigate("/kirjaudu")}>Takaisin kirjautumiseen</span>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Vaihda salasana" subtitle="Aseta uusi salasana tilillesi." testid="reset-password-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Uusi salasana" type="password" value={form.password} required
          onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reset-password" />
        <Field label="Vahvista uusi salasana" type="password" value={form.confirm} required
          onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="reset-confirm" />
        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3" data-testid="reset-error">
            <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <Btn type="submit" disabled={loading} className="w-full" data-testid="reset-submit">
          {loading ? "Vaihdetaan..." : "Vaihda salasana"}
        </Btn>
      </form>
      <p className="mt-6 text-center text-sm text-[#6E675C]">
        <span className="font-medium text-[#292827] cursor-pointer hover:underline" onClick={() => navigate("/kirjaudu")}>Takaisin kirjautumiseen</span>
      </p>
    </AuthShell>
  );
}
